"use client";

import type React from "react";
import { createContext, useContext, useState, useEffect } from "react";

// Import all translation files
import enTranslations from "./translations/en.json";
import esTranslations from "./translations/es.json";
import frTranslations from "./translations/fr.json";
import deTranslations from "./translations/de.json";
import ptTranslations from "./translations/pt.json";

export type Language = "en" | "es" | "fr" | "de" | "pt";

export const languages = {
  en: { name: "English", flag: "🇺🇸", translations: enTranslations },
  es: { name: "Español", flag: "🇪🇸", translations: esTranslations },
  fr: { name: "Français", flag: "🇫🇷", translations: frTranslations },
  de: { name: "Deutsch", flag: "🇩🇪", translations: deTranslations },
  pt: { name: "Português", flag: "🇧🇷", translations: ptTranslations },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("en");

  // Load saved language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem("bigquiz-language") as Language;
    if (savedLanguage && languages[savedLanguage]) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("bigquiz-language", language);
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = languages[language].translations;

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
